import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation, Trans } from "react-i18next";
import { toast } from "sonner";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, Link as LinkIcon, Unlink, RefreshCw, CheckCircle2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * "Link Discord" card mounted on /account.
 *
 * All UI text is driven by the `discordCard` block in the i18n JSON so
 * Spanish + French members see the whole card in their language.
 */
export default function DiscordLinkCard() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [status, setStatus]     = useState(null);
  const [loading, setLoading]   = useState(true);
  const [busy, setBusy]         = useState(false);

  const fetchStatus = async () => {
    try {
      const r = await axios.get(`${API}/api/discord/status`, { withCredentials: true });
      setStatus(r.data);
    } catch {
      setStatus({ configured: false, linked: false });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchStatus(); /* eslint-disable-next-line */ }, []);

  useEffect(() => {
    const p = new URLSearchParams(location.search);
    const d = p.get("discord");
    if (!d) return;
    // Toast keys mirror the values the OAuth callback can send back.
    const kind = {
      linked:            "success",
      needs_join:        "error",
      denied:            "info",
      expired:           "error",
      error:             "error",
      exchange_failed:   "error",
      identify_failed:   "error",
      save_failed:      "error",
    }[d];
    if (kind) {
      const msg = t(`discordCard.toasts.${d}`, { defaultValue: "" });
      if (msg) toast[kind](msg);
    }
    p.delete("discord");
    navigate({ pathname: location.pathname, search: p.toString() }, { replace: true });
    fetchStatus();
    // eslint-disable-next-line
  }, [location.search]);

  if (loading) {
    return (
      <Card>
        <CardContent className="py-6 flex items-center gap-3 text-slate-500">
          <Loader2 className="animate-spin" size={16} /> {t("discordCard.checking")}
        </CardContent>
      </Card>
    );
  }
  if (!status?.configured) return null;

  const handleLink = async () => {
    setBusy(true);
    try {
      const r = await axios.get(`${API}/api/discord/oauth/start`, { withCredentials: true });
      if (r.data?.auth_url) window.location.href = r.data.auth_url;
      else toast.error(t("discordCard.startFail"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("discordCard.linkUnavailable"));
      setBusy(false);
    }
  };

  const handleUnlink = async () => {
    if (!window.confirm(t("discordCard.unlinkConfirm"))) return;
    setBusy(true);
    try {
      await axios.post(`${API}/api/discord/unlink`, {}, { withCredentials: true });
      toast.success(t("discordCard.unlinkedToast"));
      fetchStatus();
    } catch {
      toast.error(t("discordCard.unlinkFail"));
    } finally { setBusy(false); }
  };

  const handleResync = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/api/discord/resync`, {}, { withCredentials: true });
      if (r.data?.ok) {
        const tier = (r.data.tier || user?.tier || "free").replace(/^\w/, c => c.toUpperCase());
        toast.success(t("discordCard.resyncOk", { tier }));
      } else if (r.data?.reason === "not_in_guild") toast.error(t("discordCard.resyncNotInGuild"));
      else if (r.data?.reason === "discord_not_linked") toast.error(t("discordCard.resyncNotLinked"));
      else toast.error(t("discordCard.resyncFail"));
    } catch {
      toast.error(t("discordCard.resyncFail"));
    } finally { setBusy(false); }
  };

  const tierLabel = (user?.tier || "free").replace(/^\w/, c => c.toUpperCase());

  return (
    <Card data-testid="discord-link-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <svg viewBox="0 0 24 24" width="18" height="18" className="fill-[#5865F2]" aria-hidden="true">
            <path d="M20.317 4.369A19.79 19.79 0 0 0 16.558 3c-.177.318-.383.744-.526 1.083a18.32 18.32 0 0 0-5.062 0A12.32 12.32 0 0 0 10.44 3a19.72 19.72 0 0 0-3.76 1.369C3.204 8.66 2.418 12.833 2.81 16.943a19.9 19.9 0 0 0 6.09 3.086c.494-.672.933-1.386 1.31-2.132a12.87 12.87 0 0 1-2.062-.988c.173-.127.341-.26.505-.397 3.945 1.822 8.213 1.822 12.11 0 .166.137.334.27.505.397a12.9 12.9 0 0 1-2.065.989c.377.746.815 1.46 1.31 2.131a19.86 19.86 0 0 0 6.093-3.085c.457-4.775-.783-8.912-3.29-12.574ZM9.681 14.55c-1.183 0-2.152-1.086-2.152-2.418 0-1.333.954-2.42 2.152-2.42 1.204 0 2.166 1.094 2.152 2.42.001 1.332-.948 2.418-2.152 2.418Zm7.955 0c-1.183 0-2.152-1.086-2.152-2.418 0-1.333.954-2.42 2.152-2.42 1.204 0 2.166 1.094 2.152 2.42 0 1.332-.948 2.418-2.152 2.418Z" />
          </svg>
          {t("discordCard.title")}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {status.linked ? (
          <>
            <div className="flex items-start gap-3 rounded-lg border border-emerald-200 bg-emerald-50 p-3">
              <CheckCircle2 size={18} className="text-emerald-600 mt-0.5 shrink-0" />
              <div>
                <div className="text-sm font-semibold text-emerald-900" data-testid="discord-linked-username">
                  {t("discordCard.linkedAs", { name: status.username || t("discordCard.linkedAsDefault") })}
                </div>
                <div className="text-xs text-emerald-800/80 mt-0.5">
                  <Trans i18nKey="discordCard.roleSyncNote" values={{ tier: tierLabel }} components={{ b: <strong /> }} />
                </div>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" onClick={handleResync} disabled={busy} data-testid="discord-resync-btn">
                <RefreshCw size={14} className="mr-2" /> {t("discordCard.resync")}
              </Button>
              {status.invite_url && (
                <a href={status.invite_url} target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" size="sm" data-testid="discord-open-server">{t("discordCard.openServer")}</Button>
                </a>
              )}
              <Button variant="ghost" size="sm" onClick={handleUnlink} disabled={busy} className="text-red-600 hover:text-red-700 hover:bg-red-50 ml-auto" data-testid="discord-unlink-btn">
                <Unlink size={14} className="mr-2" /> {t("discordCard.unlink")}
              </Button>
            </div>
          </>
        ) : (
          <>
            <p className="text-sm text-slate-600 leading-relaxed">
              <Trans i18nKey="discordCard.linkPrompt" values={{ tier: tierLabel }} components={{ b: <strong /> }} />
            </p>
            <Button onClick={handleLink} disabled={busy} className="bg-[#5865F2] hover:bg-[#4752C4] text-white" data-testid="discord-link-btn">
              {busy ? <Loader2 className="animate-spin mr-2" size={14} /> : <LinkIcon size={14} className="mr-2" />}
              {t("discordCard.linkButton")}
            </Button>
            <a href="/discord-guide" data-testid="discord-guide-open-link" className="block text-xs font-semibold text-[#5865F2] hover:underline">
              {t("discordGuide.newToDiscord")}
            </a>
            {status.invite_url && (
              <div className="text-xs text-slate-500">
                {t("discordCard.notInServer")}{" "}
                <a href={status.invite_url} target="_blank" rel="noopener noreferrer" className="text-[#5865F2] font-semibold underline">
                  {t("discordCard.joinFirst")}
                </a>{" "}
                {t("discordCard.joinFirstSuffix")}
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
